import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useInView } from 'motion/react';
import { ROUTES } from '@/shared/config/routes';
import { SpringboardCard } from './Card';
import { SPRINGBOARD_CARDS_V2 } from './Springboard.constants';
import { useSpringboardCards } from './useSpringboardCards';
import type { SpringboardTabLabel } from './Springboard.types';

interface SpringboardProps {
  tabs: readonly SpringboardTabLabel[];
}

export const Springboard = ({ tabs }: SpringboardProps) => {
  const { t } = useTranslation('springboard');
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.2 });
  const springboardCards = useSpringboardCards(tabs, SPRINGBOARD_CARDS_V2);

  const handleClick = (category: string) => {
    // Record where the springboard was opened from so the category page's back
    // button returns to that surface (Home today, Tools if rendered there).
    void navigate(ROUTES.TOOLS_HUB_CATEGORY(category), { state: { from: location.pathname } });
  };

  return (
    <section className="content-block gap-md" data-testid="home-tool-categories">
      <h2 className="type-heading-sm leading-8 tracking-[-0.02em]">
        {t('dashboard.springboard.browseToolsByCategories')}
      </h2>
      <div
        ref={containerRef}
        className="grid w-full grid-cols-1 gap-grid md:grid-cols-2 lg:grid-cols-4"
      >
        {springboardCards.map((card, index) => (
          <SpringboardCard
            key={card.id}
            id={card.id}
            title={card.title}
            description={card.description}
            poster={card.poster}
            video={card.video}
            icon={card.icon}
            index={index}
            isInView={isInView}
            onClick={handleClick}
          />
        ))}
      </div>
    </section>
  );
};
